import { Container, type Application } from 'pixi.js';
import type { AttentionBus } from '../bridge/AttentionBus';
import type { InputController } from '../input/InputController';
import type { SimEvent, SimState } from '../sim/types';
import type { SimView } from '../sim/view';
import type { GrayboxTextures } from './graybox';
import { AttentionLayer } from './AttentionLayer';
import { EntityLayer } from './EntityLayer';
import { OverlayLayer } from './OverlayLayer';
import { PeepLayer } from './PeepLayer';
import { TerrainLayer } from './TerrainLayer';
import { VehicleLayer } from './VehicleLayer';
import { WiresLayer } from './WiresLayer';

export class GameRenderer {
  readonly world = new Container();
  readonly terrain: TerrainLayer;
  readonly entities: EntityLayer;
  readonly peeps: PeepLayer;
  readonly wires: WiresLayer;
  readonly overlay: OverlayLayer;
  readonly attention: AttentionLayer;
  readonly vehicles: VehicleLayer;
  private readonly app: Application;
  private readonly attentionBus: AttentionBus;
  private buildingsDirty = true;

  constructor(app: Application, tex: GrayboxTextures, state: SimState, attentionBus: AttentionBus) {
    this.app = app;
    this.attentionBus = attentionBus;
    this.terrain = new TerrainLayer(tex, state.terrain);
    this.entities = new EntityLayer(tex);
    this.peeps = new PeepLayer(tex);
    this.wires = new WiresLayer(tex);
    this.overlay = new OverlayLayer(tex);
    this.attention = new AttentionLayer(tex);
    // Vehicles ride inside the entity container so painter's-order z-sorting
    // against buildings and piles just works.
    this.vehicles = new VehicleLayer(tex, this.entities.container);
    this.world.addChild(this.terrain.container);
    this.world.addChild(this.attention.haloContainer); // under buildings
    this.world.addChild(this.entities.container);
    this.world.addChild(this.peeps.container);
    this.world.addChild(this.wires.graphics);
    this.world.addChild(this.attention.badgeContainer); // above roofs
    this.world.addChild(this.overlay.container);
    app.stage.addChild(this.world);
  }

  handleEvents(events: readonly SimEvent[]): void {
    for (const event of events) {
      if (event.kind === 'tileChanged') this.terrain.markTileDirty(event.col);
      else if (event.kind === 'buildingsChanged') {
        this.buildingsDirty = true;
        this.wires.markDirty();
      } else if (event.kind === 'truckDeparted') this.vehicles.notifyDeparture('oreTruck');
      else if (event.kind === 'goodsTruckDeparted') this.vehicles.notifyDeparture('goodsWagon');
    }
  }

  draw(view: SimView, alpha: number, input: InputController): void {
    const state = view.state as SimState;
    input.camera.apply(
      this.world,
      this.app.renderer.width / this.app.renderer.resolution,
      this.app.renderer.height / this.app.renderer.resolution,
    );
    this.terrain.update(state.terrain);
    if (this.buildingsDirty) {
      this.entities.syncBuildings(state);
      this.buildingsDirty = false;
    }
    this.entities.syncPiles(state);
    const simSeconds = (state.meta.tick + alpha) * 0.1;
    this.entities.animate(simSeconds);
    this.vehicles.update(state, simSeconds);
    this.attention.update(state, performance.now() / 1000, this.attentionBus.highlights);
    this.wires.update(state);
    this.peeps.update(state, alpha);
    this.overlay.update(view, input);
  }
}
